import { useCallback, useRef, useState, type DragEvent } from "react";
import { Crop, FolderOpen, Loader2, UploadCloud } from "lucide-react";
import { MediaLibrary } from "./MediaLibrary";
import { CropModal } from "./CropModal";
import type { CropConstraint } from "./cropConstraints";
import type { PixelCrop } from "./getCroppedImg";

interface ImageUploadFieldProps {
  /** Human-readable label, e.g. "Background image" */
  label: string;
  /** Current image URL, or empty/undefined for empty state */
  value: string | undefined;
  /** Called with the CDN url on successful upload, or "" on remove */
  onChange: (url: string) => void;
  /** Crop constraint -- when provided, shows the Crop button */
  cropConstraint?: CropConstraint | null;
  /** Original image URL for re-cropping */
  imageOriginalUrl?: string;
  /** Storage key of original (passed through, not consumed) */
  imageOriginalStorageKey?: string;
  /** Saved crop state for re-editing */
  imageCrop?: { x: number; y: number; width: number; height: number; zoom: number };
  /** Called with crop metadata after a successful crop + upload */
  onCropMetadataChange?: (meta: {
    imageOriginalUrl: string;
    imageOriginalStorageKey: string | undefined;
    imageCrop: { x: number; y: number; width: number; height: number; zoom: number };
  }) => void;
}

/**
 * Image upload field with empty / uploading / filled states,
 * plus a "Browse library" button to pick from previously uploaded images.
 */
export function ImageUploadField({
  label,
  value,
  onChange,
  cropConstraint,
  imageOriginalUrl,
  imageOriginalStorageKey,
  imageCrop,
  onCropMetadataChange,
}: ImageUploadFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);

  const upload = useCallback(
    async (file: File) => {
      setError(null);
      const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
      const MAX_SIZE_MB = 10;
      if (!ACCEPTED_TYPES.includes(file.type)) {
        setError("Only JPG, PNG, and WebP images are accepted");
        return;
      }
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        setError(`Image must be under ${MAX_SIZE_MB} MB`);
        return;
      }
      setIsUploading(true);
      try {
        const form = new FormData();
        form.append("file", file);
        const res = await fetch("/api/pages/upload-image", {
          method: "POST",
          body: form,
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(
            (body as { error?: string }).error ||
              `Upload failed (${res.status})`,
          );
        }
        const data = (await res.json()) as { cdnUrl: string };
        onChange(data.cdnUrl);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Upload failed");
      } finally {
        setIsUploading(false);
      }
    },
    [onChange],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) void upload(file);
      // Reset so re-selecting the same file still triggers onChange
      e.target.value = "";
    },
    [upload],
  );

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) void upload(file);
    },
    [upload],
  );

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleLibrarySelect = useCallback(
    (cdnUrl: string, _altText: string | null) => {
      onChange(cdnUrl);
    },
    [onChange],
  );

  const handleCropApply = useCallback(
    async (blob: Blob, cropArea: PixelCrop, zoom: number) => {
      setError(null);
      setIsUploading(true);
      try {
        const file = new File([blob], "cropped.webp", { type: "image/webp" });
        const form = new FormData();
        form.append("file", file);
        const res = await fetch("/api/pages/upload-image", {
          method: "POST",
          body: form,
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(
            (body as { error?: string }).error ||
              `Upload failed (${res.status})`,
          );
        }
        const data = (await res.json()) as { cdnUrl: string };
        onChange(data.cdnUrl);
        // Persist crop metadata for re-editing
        onCropMetadataChange?.({
          imageOriginalUrl: imageOriginalUrl || value || "",
          imageOriginalStorageKey,
          imageCrop: {
            x: cropArea.x,
            y: cropArea.y,
            width: cropArea.width,
            height: cropArea.height,
            zoom,
          },
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : "Upload failed");
      } finally {
        setIsUploading(false);
      }
    },
    [onChange, onCropMetadataChange, imageOriginalUrl, imageOriginalStorageKey, value],
  );

  const hasImage = !!value;

  // Try to extract a display filename from a URL
  const displayName = value
    ? decodeURIComponent(value.split("/").pop() || "image")
    : "";

  return (
    <div className="mb-[18px]">
      <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
        {label}
      </label>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFileChange}
      />

      {isUploading ? (
        /* Uploading state */
        <div className="flex items-center justify-center border border-dashed border-ink-6 rounded-[12px] bg-paper py-[18px]">
          <Loader2
            size={20}
            strokeWidth={2}
            className="text-tide animate-spin"
          />
          <span className="ml-2 text-[12px] font-semibold text-ink-4">
            Uploading...
          </span>
        </div>
      ) : hasImage ? (
        /* Filled state */
        <div className="border border-ink-6 rounded-[12px] overflow-hidden bg-paper">
          <div
            className="h-[92px] bg-center bg-cover bg-no-repeat"
            style={{ backgroundImage: `url("${value}")` }}
          />
          <div className="px-2.5 py-2">
            {displayName && (
              <div className="mb-1.5 font-mono text-[10.5px] text-ink-4 overflow-hidden text-ellipsis whitespace-nowrap">
                {displayName}
              </div>
            )}
            {/* Even grid so the action row can never overflow the narrow inspector */}
            <div
              className={`grid ${cropConstraint ? "grid-cols-4" : "grid-cols-3"} gap-1`}
            >
              <button
                type="button"
                className="min-w-0 flex items-center justify-center border-0 bg-transparent text-[11px] font-semibold text-tide px-1 py-1 rounded-[6px] hover:bg-tide-ss transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                Replace
              </button>
              <button
                type="button"
                className="min-w-0 flex items-center justify-center border-0 bg-transparent text-[11px] font-semibold text-tide px-1 py-1 rounded-[6px] hover:bg-tide-ss transition-colors"
                onClick={() => setLibraryOpen(true)}
              >
                Library
              </button>
              {cropConstraint && (
                <button
                  type="button"
                  className="min-w-0 flex items-center justify-center gap-1 border-0 bg-transparent text-[11px] font-semibold text-tide px-1 py-1 rounded-[6px] hover:bg-tide-ss transition-colors"
                  onClick={() => setCropOpen(true)}
                >
                  <Crop size={11} strokeWidth={2} />
                  Crop
                </button>
              )}
              <button
                type="button"
                className="min-w-0 flex items-center justify-center border-0 bg-transparent text-[11px] font-semibold text-rust px-1 py-1 rounded-[6px] hover:bg-rust-s transition-colors"
                onClick={() => onChange("")}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Empty state */
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`w-full flex flex-col items-center gap-[7px] border-[1.5px] border-dashed rounded-[12px] bg-paper px-3 py-[18px] text-center text-ink-4 text-[12px] cursor-pointer transition-all duration-100 ${
              isDragOver
                ? "border-tide bg-tide-ss text-tide-d"
                : "border-ink-6 hover:border-tide hover:bg-tide-ss hover:text-tide-d"
            }`}
          >
            <UploadCloud size={20} strokeWidth={1.6} className="opacity-70" />
            <b className="font-semibold">Upload image</b>
            <small className="text-[10.5px] text-ink-5">
              or drag &amp; drop -- JPG, PNG, WebP
            </small>
          </button>
          <button
            type="button"
            onClick={() => setLibraryOpen(true)}
            className="w-full flex items-center justify-center gap-1.5 border border-ink-6 rounded-[10px] bg-paper px-3 py-2 text-[12px] font-semibold text-ink-3 hover:border-tide hover:text-tide-d hover:bg-tide-ss transition-all cursor-pointer"
          >
            <FolderOpen size={14} strokeWidth={1.8} />
            Browse library
          </button>
        </div>
      )}

      {error && (
        <p className="mt-1.5 text-[11px] text-rust font-medium">{error}</p>
      )}

      <MediaLibrary
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={handleLibrarySelect}
      />

      {cropConstraint && value && (
        <CropModal
          open={cropOpen}
          onClose={() => setCropOpen(false)}
          imageSrc={
            imageOriginalUrl && imageCrop
              ? imageOriginalUrl
              : value
          }
          constraint={cropConstraint}
          initialCrop={
            imageOriginalUrl && imageCrop
              ? { x: 0, y: 0, zoom: imageCrop.zoom }
              : undefined
          }
          onApply={handleCropApply}
        />
      )}
    </div>
  );
}
